import { SLOT_SYMBOL_IMAGES } from "../../../../../static/Constants";
import { colors, borderRadiusPx, customShadows, THREESLOT_COLOR_THEME } from "../../../../../theme";

/**
 * 3-Slot Detail Page Theme & Layout Config (Centralized Tokens)
 */
export const THREE_SLOT_DETAILS_THEME = {
  pageBg: colors.dashboard.canvas,
  cardMaxWidth: 1040,
  containerMaxWidth: "lg",
  headerBorderRadius: borderRadiusPx.xl,
  machineBorderRadius: borderRadiusPx.xxl,
  shadow: customShadows.card,
  hoverShadow: customShadows.cardHover,
  colorTheme: THREESLOT_COLOR_THEME,
};

/**
 * Centralized Progressive Jackpot Tier Themes (Mega, Major, Mini)
 * Styled with light, clean surfaces and vibrant lottery accent borders & text
 */
export const JACKPOT_TIER_THEMES = {
  mega: {
    id: "mega",
    label: "👑 MEGA JACKPOT",
    background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
    border: "1.5px solid #fca5a5",
    borderColor: "#fca5a5",
    boxShadow: "0 2px 8px rgba(239, 68, 68, 0.12)",
    titleColor: "#dc2626",
    valueColor: "#991b1b",
    borderRadius: borderRadiusPx.md || "10px",
  },
  major: {
    id: "major",
    label: "💎 MAJOR JACKPOT",
    background: "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)",
    border: "1.5px solid #d8b4fe",
    borderColor: "#d8b4fe",
    boxShadow: "0 2px 8px rgba(168, 85, 247, 0.12)",
    titleColor: "#7c3aed",
    valueColor: "#581c87",
    borderRadius: borderRadiusPx.md || "10px",
  },
  mini: {
    id: "mini",
    label: "🔔 MINI JACKPOT",
    background: "linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)",
    border: "1.5px solid #fde047",
    borderColor: "#fde047",
    boxShadow: "0 2px 8px rgba(234, 179, 8, 0.12)",
    titleColor: "#ca8a04",
    valueColor: "#854d0e",
    borderRadius: borderRadiusPx.md || "10px",
  },
};

/**
 * Side Badge Numbers with Casino Neon Colors
 */
export const SIDE_BADGES = [
  { num: "4", color: colors.red[500] || "#ef4444" },
  { num: "2", color: colors.blue[500] || "#3b82f6" },
  { num: "9", color: colors.emerald[500] || "#22c55e" },
  { num: "6", color: colors.amber[500] || "#eab308" },
  { num: "1", color: colors.purple[500] || "#a855f7" },
  { num: "7", color: "#f97316" },
  { num: "8", color: colors.cyan[500] || "#06b6d4" },
  { num: "3", color: "#ec4899" },
  { num: "5", color: colors.blue[500] || "#3b82f6" },
];

/**
 * Slot Machine Arcade Frame Theme Tokens (Consistent with Platform Card Design)
 */
export const SLOT_MACHINE_FRAME_THEME = {
  bg: "#ffffff",
  headerBg: "#f8fafc",
  border: "1px solid #e2e8f0",
  boxShadow: customShadows.cardFlat || "0 4px 20px rgba(0, 0, 0, 0.05)",
  borderRadius: { xs: borderRadiusPx.lg || "16px", sm: borderRadiusPx.xl || "20px" },
  headerBorderRadius: borderRadiusPx.md || "8px",
  goldBorder: "#eab308",
  goldText: "#b45309",
  amberAccent: "#f59e0b",
};

/**
 * 3-SlotBET Game Configuration
 * Fiat & Crypto 5-Symbol Editions (Contract Mapped 0..4)
 */
export const THREE_SLOT_CONFIG = {
  name: "3-SlotBET",
  subtitle: "PROGRESSIVE SLOT",
  reels: 3,
  rows: 3,
  paylines: 1,
  paylineDescription: "Match 3 symbols on the center payline to win!",
  initialReels: ["bell", "diamond", "crown"],
  defaultBet: 5.0,
  minBet: 1.0,
  maxBet: 500.0,
  betSteps: [1, 2, 5, 10, 25, 50, 100, 250, 500],
  currencySymbol: "BET",
  currencyName: "BET",
};

export const JACKPOT_DEFAULTS = {
  mega: 50000.0,
  major: 10000.0,
  mini: 1000.0,
};

export const THEME_OPTIONS = [
  {
    id: "fiat",
    label: "High-Roller Vegas Slot",
    description: "Authentic casino symbols: Golden Bell (Mini), Brilliant Diamond (Major), Royal Crown (Mega), Casino Cherries (5 Spins), Lucky 7 (10 Super Spins)",
  },
  {
    id: "crypto",
    label: "Crypto Slot",
    description: "Authentic 3D crypto coins: Polygon POL (Mini), Ethereum ETH (Major), Bitcoin BTC (Mega), Solana SOL (5 Spins), Tether USDT (10 Super Spins)",
  },
];

/**
 * Authentic Casino Gambling 5-Symbol Set (Contract Mapped 0 to 4)
 * 0: Mini Jackpot (Min Bet) -> Golden Liberty Bell
 * 1: Major Jackpot -> Brilliant Diamond
 * 2: Mega Jackpot -> Royal Crown
 * 3: 5 Free Spins -> Casino Cherries
 * 4: 10 Super Free Spins -> Lucky 7
 */
export const FIAT_SET_SYMBOLS = [
  {
    contractNumber: 0,
    id: "bell",
    label: "Golden Bell",
    winningTier: "Mini Jackpot",
    winOutcome: "Mini Bet / Mini Jackpot",
    code: "BELL",
    symbolText: "🔔",
    iconText: "🔔",
    multiplier: 10,
    tier: "Mini Jackpot",
    tierColor: "#eab308",
    color: "#ca8a04",
    type: "fiat",
    category: "fiat",
    image: SLOT_SYMBOL_IMAGES.bell,
    payout: "Mini Jackpot (Min Bet Win)",
    description: "Match 3 Golden Bells to win Mini Progressive Jackpot & Min Bet payout",
  },
  {
    contractNumber: 1,
    id: "diamond",
    label: "Brilliant Diamond",
    winningTier: "Major Jackpot",
    winOutcome: "Major Jackpot",
    code: "DIAMOND",
    symbolText: "💎",
    iconText: "💎",
    multiplier: 30,
    tier: "Major Jackpot",
    tierColor: "#3b82f6",
    color: "#2563eb",
    type: "fiat",
    category: "fiat",
    image: SLOT_SYMBOL_IMAGES.diamond,
    payout: "Major Jackpot",
    description: "Match 3 Brilliant Diamonds to win Major Progressive Jackpot",
  },
  {
    contractNumber: 2,
    id: "crown",
    label: "Royal Crown",
    winningTier: "Mega Jackpot",
    winOutcome: "Mega Jackpot",
    code: "CROWN",
    symbolText: "👑",
    iconText: "👑",
    multiplier: 50,
    tier: "Mega Jackpot",
    tierColor: "#f59e0b",
    color: "#d97706",
    type: "fiat",
    category: "fiat",
    image: SLOT_SYMBOL_IMAGES.crown,
    payout: "Mega Jackpot",
    description: "Match 3 Royal Crowns to win Grand Mega Progressive Jackpot",
  },
  {
    contractNumber: 3,
    id: "cherries",
    label: "Casino Cherries",
    winningTier: "5 Free Spins",
    winOutcome: "5 Free Spins",
    code: "CHERRY",
    symbolText: "🍒",
    iconText: "🍒",
    multiplier: 15,
    tier: "5 Free Spins",
    tierColor: "#ef4444",
    color: "#dc2626",
    type: "fiat",
    category: "fiat",
    image: SLOT_SYMBOL_IMAGES.cherries,
    payout: "5 Free Spins",
    description: "Match 3 Casino Cherries to trigger 5 Free Spins Bonus Round",
  },
  {
    contractNumber: 4,
    id: "lucky_7",
    label: "Lucky 7",
    winningTier: "10 Super Spins",
    winOutcome: "10 Super Spins",
    code: "SEVEN",
    symbolText: "7️⃣",
    iconText: "7️⃣",
    multiplier: 25,
    tier: "10 Super Spins",
    tierColor: "#dc2626",
    color: "#b91c1c",
    type: "fiat",
    category: "fiat",
    image: SLOT_SYMBOL_IMAGES.lucky_7,
    payout: "10 Super Spins",
    description: "Match 3 Lucky 7s for 10 Super Free Spins Bonus Round",
  },
];

/**
 * CRYPTO Slot 5-Symbol Set (Contract Mapped 0 to 4)
 * 0: Mini Jackpot (Min Bet) -> Polygon POL
 * 1: Major Jackpot -> Ethereum ETH
 * 2: Mega Jackpot -> Bitcoin BTC
 * 3: 5 Free Spins -> Solana SOL
 * 4: 10 Super Free Spins -> Tether USDT
 */
export const CRYPTO_SET_SYMBOLS = [
  {
    contractNumber: 0,
    id: "pol",
    label: "Polygon POL",
    winningTier: "Mini Jackpot",
    winOutcome: "Mini Bet / Mini Jackpot",
    code: "POL",
    symbolText: "🟣",
    iconText: "🟣",
    multiplier: 10,
    tier: "Mini Jackpot",
    tierColor: "#a855f7",
    color: "#9333ea",
    type: "crypto",
    category: "crypto",
    image: SLOT_SYMBOL_IMAGES.pol,
    payout: "Mini Jackpot (Min Bet Win)",
    description: "Match 3 POL to win Mini Progressive Jackpot & Min Bet payout",
  },
  {
    contractNumber: 1,
    id: "eth",
    label: "Ethereum ETH",
    winningTier: "Major Jackpot",
    winOutcome: "Major Jackpot",
    code: "ETH",
    symbolText: "🔷",
    iconText: "🔷",
    multiplier: 30,
    tier: "Major Jackpot",
    tierColor: "#3b82f6",
    color: "#2563eb",
    type: "crypto",
    category: "crypto",
    image: SLOT_SYMBOL_IMAGES.eth,
    payout: "Major Jackpot",
    description: "Match 3 Ethereum coins to win Major Progressive Jackpot",
  },
  {
    contractNumber: 2,
    id: "btc",
    label: "Bitcoin BTC",
    winningTier: "Mega Jackpot",
    winOutcome: "Mega Jackpot",
    code: "BTC",
    symbolText: "₿",
    iconText: "₿",
    multiplier: 50,
    tier: "Mega Jackpot",
    tierColor: "#f59e0b",
    color: "#d97706",
    type: "crypto",
    category: "crypto",
    image: SLOT_SYMBOL_IMAGES.btc,
    payout: "Mega Jackpot",
    description: "Match 3 Bitcoins to win Grand Mega Progressive Jackpot",
  },
  {
    contractNumber: 3,
    id: "sol",
    label: "Solana SOL",
    winningTier: "5 Free Spins",
    winOutcome: "5 Free Spins",
    code: "SOL",
    symbolText: "🟣",
    iconText: "🟣",
    multiplier: 15,
    tier: "5 Free Spins",
    tierColor: "#06b6d4",
    color: "#0891b2",
    type: "crypto",
    category: "crypto",
    image: SLOT_SYMBOL_IMAGES.sol,
    payout: "5 Free Spins",
    description: "Match 3 Solana coins to trigger 5 Free Spins Web3 Bonus Round",
  },
  {
    contractNumber: 4,
    id: "usdt",
    label: "Tether USDT",
    winningTier: "10 Super Spins",
    winOutcome: "10 Super Spins",
    code: "USDT",
    symbolText: "🟢",
    iconText: "🟢",
    multiplier: 25,
    tier: "Super Spins",
    tierColor: "#10b981",
    color: "#059669",
    type: "crypto",
    category: "crypto",
    image: SLOT_SYMBOL_IMAGES.usdt,
    payout: "10 Super Spins",
    description: "Match 3 Tether USDT coins for 10 Super Free Spins Bonus Round",
  },
];

export const ALL_SYMBOLS = [...FIAT_SET_SYMBOLS, ...CRYPTO_SET_SYMBOLS];
export const SYMBOLS = ALL_SYMBOLS;

/**
 * Helper to get the 5 symbols mapped from 0 to 4 based on Fiat or Crypto category
 */
export const getSymbolsByCategory = (category = "fiat") => {
  if (category === "crypto") {
    return CRYPTO_SET_SYMBOLS;
  }
  return FIAT_SET_SYMBOLS;
};

export const getSymbolsByTheme = (themeName = "fiat") => {
  return getSymbolsByCategory(themeName);
};

export const getRandomSymbol = (category = "fiat") => {
  const symbolSet = getSymbolsByCategory(category);
  const randomIndex = Math.floor(Math.random() * symbolSet.length);
  return symbolSet[randomIndex] || symbolSet[0];
};

export const getSymbolById = (id) => {
  return ALL_SYMBOLS.find((symbol) => symbol.id === id) || FIAT_SET_SYMBOLS[0];
};

/**
 * Determine winning payout and jackpot status for 3-Reel Slot
 * Evaluates center 3 symbols (Match 3)
 */
export const calculateWinResult = (
  reels,
  betAmount = 5.0,
  isFreeSpins = false,
  jackpots = JACKPOT_DEFAULTS
) => {
  if (!reels || reels.length !== 3) {
    return { isWin: false, amount: 0, type: "none", label: "No Spin" };
  }

  const [s1, s2, s3] = reels;
  const multiplierMultiplier = isFreeSpins ? 2 : 1; // 2x boost during Free Spins

  // Check 3 of a kind exact match
  const isMatch3 =
    (s1.contractNumber !== undefined &&
      s1.contractNumber === s2.contractNumber &&
      s2.contractNumber === s3.contractNumber) ||
    (s1.id && s1.id === s2.id && s2.id === s3.id);

  if (isMatch3) {
    const contractNum = s1.contractNumber;

    // Index 0: Mini Jackpot (Min Bet Win)
    if (contractNum === 0 || s1.id === "bell" || s1.id === "emerald" || s1.id === "pol") {
      const winAmt =
        jackpots?.mini && jackpots.mini > 0
          ? jackpots.mini
          : betAmount * s1.multiplier * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "jackpot",
        jackpotTier: "mini",
        label: "MINI JACKPOT!",
        symbol: s1,
      };
    }

    // Index 1: Major Jackpot
    if (contractNum === 1 || s1.id === "diamond" || s1.id === "eth" || s1.id === "gold_nugget") {
      const winAmt =
        jackpots?.major && jackpots.major > 0
          ? jackpots.major
          : betAmount * s1.multiplier * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "jackpot",
        jackpotTier: "major",
        label: "MAJOR JACKPOT!",
        symbol: s1,
      };
    }

    // Index 2: Mega Jackpot
    if (contractNum === 2 || s1.id === "crown" || s1.id === "jackpot_crown" || s1.id === "btc") {
      const winAmt =
        jackpots?.mega && jackpots.mega > 0
          ? jackpots.mega
          : betAmount * s1.multiplier * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "jackpot",
        jackpotTier: "mega",
        label: "MEGA JACKPOT!",
        symbol: s1,
      };
    }

    // Index 3: 5 Free Spins
    if (contractNum === 3 || s1.id === "cherries" || s1.id === "sol" || s1.id === "cherry" || s1.id === "ruby" || s1.id === "kundu_drum" || s1.id === "lucky_7") {
      const winAmt = betAmount * s1.multiplier * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "freespins",
        freeSpinsWon: 5,
        label: "5 FREE SPINS WON!",
        jackpotTier: null,
        symbol: s1,
      };
    }

    // Index 4: 10 Super Free Spins (2x Multiplier)
    if (contractNum === 4 || s1.id === "usdt" || s1.id === "lucky_7" || s1.id === "seven" || s1.id === "clover" || s1.id === "amethyst" || s1.id === "kumul_bird" || s1.id === "jackpot_orb") {
      const winAmt = betAmount * s1.multiplier * 2 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "freespins",
        freeSpinsWon: 10,
        label: "10 SUPER SPINS (2X WIN)!",
        jackpotTier: null,
        symbol: s1,
      };
    }

    // Fallback match 3
    const winAmt = betAmount * (s1.multiplier || 10) * multiplierMultiplier;
    return {
      isWin: true,
      amount: winAmt,
      type: "match3",
      jackpotTier: null,
      label: `3x ${s1.label.toUpperCase()}`,
      symbol: s1,
    };
  }

  // No match
  return {
    isWin: false,
    amount: 0,
    type: "loss",
    jackpotTier: null,
    label: "No Match",
  };
};